/**
 * BulkMarkAvailabilityAction — bulk "Zablokuj dostępność" action for the
 * media-units catalog list.
 *
 * Reuses the bulk-edit-media selection pattern verbatim: operates on the
 * caller's explicit checkbox selection (selectedIds) when present, otherwise
 * falls back to the current catalog filter (activeFilters) — mirroring
 * BulkEditSelectionSchema's `{ ids } | { filter }` union.
 *
 * The "Zablokuj dostępność" trigger lives in the shared floating
 * `BulkActionToolbar` (rendered by the route); this component owns only the
 * create-window-style FREE/UNAVAILABLE dialog, controlled by the caller's
 * `isOpen`/`onOpenChange` props. This keeps exactly one selection affordance
 * and one floating toolbar on the catalog list instead of a separate
 * below-table trigger button. Because the toolbar only renders once the
 * shared selection is non-empty (explicit ids or an active "select all N
 * matching" filter), the filter fallback above is now reachable only through
 * the explicit "select all N matching" affordance, never as a silent
 * zero-selection default — see the updated `availability-windows` spec.
 *
 * This component stays permanently mounted in the route tree regardless of
 * `isOpen` (only its rendered output is gated), so the route remounts it via
 * a changing `key` on every open to guarantee fresh form state — an
 * effect-based reset would flash the previous session's stale values for one
 * paint before clearing them.
 *
 * Owns the dialog's internal form state (with half-month presets), the
 * bulk-mark mutation call, and the updated/failed result toast. On request
 * failure the dialog stays open with an inline server error (mirrors the
 * availability-windows single-unit dialog's "keep the dialog open"
 * convention) instead of closing.
 *
 * Traceability: availability-windows / Requirement: Bulk mark-from-list entry
 * point on the catalog list.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';
import { parseApiError } from '@ooh/contracts';
import type { MediaUnitFilters, AvailabilityWindowState } from '@ooh/contracts';
import { isBulkSelectionCapExceeded, shouldShowHiddenCount } from '../lib/bulk-selection-cap';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { AvailabilityWindowFormFields, isAvailabilityWindowFormValid } from '@ooh/screens';
import { useBulkMarkAvailabilityMutation } from '../api/mutations';
import { buildHalfMonthRange } from '../lib/build-half-month-range';
import type { HalfMonthPreset } from '../lib/build-half-month-range';

export interface BulkMarkAvailabilityActionProps {
  /** Holder of media_unit.block_availability — renders the dialog. */
  canBulkMarkAvailability: boolean;
  /** Controlled open state, driven by the shared BulkActionToolbar's "Zablokuj dostępność" action. */
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Explicit checkbox selection (useMediaBulkSelection.selectedIds). */
  selectedIds: number[];
  /** Current catalog filter — used as the selection when no ids are checked. */
  activeFilters: MediaUnitFilters;
  /** Total rows matching the current filter (for the confirm count + cap check). */
  totalMatching: number;
  /**
   * Of `selectedIds`, how many are not in the current results (id-based
   * selection only — the filter fallback has no concept of "hidden").
   */
  hiddenCount?: number;
}

// ─── Dialog body (presentational) ──────────────────────────────

interface BulkMarkAvailabilityDialogProps {
  t: TFunction<'inventory'>;
  dateFrom: string;
  dateTo: string;
  state: AvailabilityWindowState | '';
  effectiveCount: number;
  hiddenCount: number;
  isFilterSelection: boolean;
  capExceeded: boolean;
  serverError: string | null;
  isPending: boolean;
  isValid: boolean;
  onRangeChange: (next: { from: string; to: string }) => void;
  onStateChange: (value: AvailabilityWindowState) => void;
  onPreset: (preset: HalfMonthPreset) => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function BulkMarkAvailabilityDialog({
  t,
  dateFrom,
  dateTo,
  state,
  effectiveCount,
  hiddenCount,
  isFilterSelection,
  capExceeded,
  serverError,
  isPending,
  isValid,
  onRangeChange,
  onStateChange,
  onPreset,
  onCancel,
  onSubmit,
}: BulkMarkAvailabilityDialogProps) {
  return (
    <ModalDialog
      header={
        <span className="text-base font-semibold">{t('bulkMarkAvailability.drawerTitle')}</span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="bulk-mark-availability-cancel-btn"
            onClick={onCancel}
            disabled={isPending}
          >
            {t('bulkMarkAvailability.cancel')}
          </Button>
          <Button
            size="sm"
            data-testid="bulk-mark-availability-submit-btn"
            onClick={onSubmit}
            disabled={!isValid || capExceeded || isPending}
          >
            {isPending ? '...' : t('bulkMarkAvailability.submit')}
          </Button>
        </div>
      }
      onClose={onCancel}
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="bulk-mark-availability-preset-first-half"
            onClick={() => onPreset('first-half')}
          >
            {t('bulkMarkAvailability.presetFirstHalf')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="bulk-mark-availability-preset-second-half"
            onClick={() => onPreset('second-half')}
          >
            {t('bulkMarkAvailability.presetSecondHalf')}
          </Button>
        </div>
        <AvailabilityWindowFormFields
          labels={{
            dateRangeLabel: t('bulkMarkAvailability.dateRangeLabel'),
            dateRangePlaceholder: t('bulkMarkAvailability.dateRangePlaceholder'),
            stateLabel: t('bulkMarkAvailability.stateLabel'),
            freeOptionLabel: t('bulkMarkAvailability.state.FREE'),
            unavailableOptionLabel: t('bulkMarkAvailability.state.UNAVAILABLE'),
            statePlaceholder: t('bulkMarkAvailability.statePlaceholder'),
          }}
          dateFrom={dateFrom}
          dateTo={dateTo}
          state={state}
          onRangeChange={onRangeChange}
          onStateChange={onStateChange}
          testIds={{
            // eslint-disable-next-line i18next/no-literal-string -- test id, not user-facing
            dateRange: 'bulk-mark-availability-date-range',
            // eslint-disable-next-line i18next/no-literal-string -- test id, not user-facing
            stateSelect: 'bulk-mark-availability-state-select',
          }}
        />
        <div
          className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm"
          data-testid="bulk-mark-availability-count"
        >
          {t('bulkMarkAvailability.confirmMessage', { count: effectiveCount })}
          {shouldShowHiddenCount(isFilterSelection, hiddenCount) ? (
            <div data-testid="bulk-mark-availability-hidden-count">
              {t('bulkMarkAvailability.hiddenCount', { count: hiddenCount })}
            </div>
          ) : null}
        </div>
        {capExceeded ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="bulk-mark-availability-cap-warning"
          >
            {t('bulkMarkAvailability.capWarning')}
          </div>
        ) : null}
        {serverError ? (
          <div
            className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            data-testid="bulk-mark-availability-server-error"
          >
            {serverError}
          </div>
        ) : null}
      </div>
    </ModalDialog>
  );
}

// ─── Main component ───────────────────────────────────────────

export function BulkMarkAvailabilityAction({
  canBulkMarkAvailability,
  isOpen,
  onOpenChange,
  selectedIds,
  activeFilters,
  totalMatching,
  hiddenCount = 0,
}: BulkMarkAvailabilityActionProps) {
  const { t } = useTranslation('inventory');
  const mutation = useBulkMarkAvailabilityMutation();

  const [dateFrom, setDateFrom] = React.useState('');
  const [dateTo, setDateTo] = React.useState('');
  const [state, setState] = React.useState<AvailabilityWindowState | ''>('');
  const [serverError, setServerError] = React.useState<string | null>(null);

  function resetForm() {
    setDateFrom('');
    setDateTo('');
    setState('');
    setServerError(null);
  }

  if (!canBulkMarkAvailability) return null;

  const isIdSelection = selectedIds.length > 0;
  const effectiveCount = isIdSelection ? selectedIds.length : totalMatching;
  const capExceeded = isBulkSelectionCapExceeded(!isIdSelection, totalMatching, selectedIds.length);
  const isValid = isAvailabilityWindowFormValid(dateFrom, dateTo, state);

  function handlePreset(preset: HalfMonthPreset) {
    const anchor = dateFrom ? new Date(dateFrom) : new Date();
    const range = buildHalfMonthRange(anchor, preset);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
  }

  async function handleSubmit() {
    if (!isValid || capExceeded || state === '') return;
    setServerError(null);
    try {
      const response = await mutation.mutateAsync({
        selection: isIdSelection ? { ids: selectedIds } : { filter: activeFilters },
        dateFrom: new Date(dateFrom),
        dateTo: new Date(dateTo),
        state,
      });
      toast.success(
        t('bulkMarkAvailability.resultToast', {
          updated: response.updated,
          failed: response.failed,
        }),
      );
      onOpenChange(false);
      resetForm();
    } catch (error) {
      setServerError(parseApiError(error).message);
    }
  }

  if (!isOpen) return null;

  return (
    <BulkMarkAvailabilityDialog
      t={t}
      dateFrom={dateFrom}
      dateTo={dateTo}
      state={state}
      effectiveCount={effectiveCount}
      hiddenCount={hiddenCount}
      isFilterSelection={!isIdSelection}
      capExceeded={capExceeded}
      serverError={serverError}
      isPending={mutation.isPending}
      isValid={isValid}
      onRangeChange={({ from, to }) => {
        setDateFrom(from);
        setDateTo(to);
      }}
      onStateChange={setState}
      onPreset={handlePreset}
      onCancel={() => onOpenChange(false)}
      onSubmit={() => void handleSubmit()}
    />
  );
}
